import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import {
  CardActions,
  Fab,
  Card,
  Button,
  Box,
  Tooltip,
  Paper,
  FormControlLabel,
  Checkbox,
  Stack,
  Menu,
  Typography,
  IconButton,
  Chip,
  Collapse
} from '@mui/material'
import Grid2 from '@mui/material/Grid2'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import { IconContext } from 'react-icons'
import { AiOutlineAudit } from 'react-icons/ai'
import { useLocation } from 'react-router-dom'
import Link from '../navigation/Link'
import useNavigate from '../hooks/useNavigate'
import FilesDialog from '../dialogs/FilesDialog'
import AuditDialog from '../dialogs/AuditDialog'
import { QueryBuilderMaterial } from '@react-querybuilder/material'
import { QueryBuilder, formatQuery } from 'react-querybuilder'
import 'react-querybuilder/dist/query-builder-layout.css'
import { CustomDateEditor } from '../fields/CustomDateEditor'
import EmailDialog from '../dialogs/EmailDialog'
import { parseMongoDB } from 'react-querybuilder/parseMongoDB'
import { useAuth } from '../../context/AuthContext'

const MenuVisibility = React.memo(function MenuVisibility({
  isMenuOpen,
  anchorEl,
  handleCloseMenu,
  columns = [],
  changeVisibility,
  resetColumns
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', mx: '5px' }}>
        {columns.map((col) => (
          <FormControlLabel
            key={col.name}
            control={
              <Checkbox
                name={col.name}
                checked={col.isVisible ?? true}
                onChange={(e) => changeVisibility(e, col.name)}
              />
            }
            label={col.label}
          />
        ))}
        <Button
          variant='outlined'
          size='small'
          color='secondary'
          onClick={() => {
            resetColumns()
            handleCloseMenu()
          }}
          sx={{ mt: 1 }}
        >
          Réinitialiser
        </Button>
      </Box>
    </Menu>
  )
})

const useGlobalQueryBuilderStyles = createUseStyles({
  '@global': {
    '.ruleGroup': { backgroundColor: 'white' },
    '.ruleGroup .ruleGroup, .ruleGroup .rule': {
      borderRadius: '4px',
      border: '1px solid #ddd',
      boxShadow: '0 1px 1px rgba(0,0,0,.05)'
    },
    '.ruleGroup .rule': {
      backgroundColor: '#f5f5f5',
      borderLeft: '2px solid #3f51b5',
      padding: '0.5rem'
    },
    '.ruleGroup .ruleGroup': { borderLeft: '2px solid #3f51b5' },
    '.ruleGroup-remove, .rule-remove': {
      float: 'right',
      backgroundColor: 'secondary',
      border: 'none'
    },
    '.ruleGroup-combinators,  .rule-fields, .rule-operators, .rule-value': {
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      padding: '3.5px 12px'
    },
    '.ruleGroup-addRule, .ruleGroup-addGroup': {
      backgroundColor: '#3f51b5',
      border: '1px solid #ddd',
      borderRadius: '4px',
      padding: '6px 12px',
      '&:hover': { backgroundColor: 'rgb(44, 56, 126)' }
    }
  }
})

function Toolbar({
  options,
  object,
  setObject,
  setFilters = () => {},
  setIsUpdated,
  parentObject,
  setParentObject,
  setColumnsParent
}) {
  const muiTheme = useTheme()
  const style = options.style || { width: '90%', marginLeft: '5%', marginTop: '5px', marginBottom: '5px' }
  const type = options?.objectType
  const modelType = options?.modelType

  useGlobalQueryBuilderStyles()

  const { user } = useAuth()
  const userId = user?.id || 'anon'

  const getPrefKey = (baseKey) => `${baseKey}_${type}_${userId}`

  const location = useLocation()
  const navigate = useNavigate()

  const [showFilters, setShowFilters] = useState(false)
  const [open, setOpen] = useState(false)
  const [openAudit, setOpenAudit] = useState(false)
  const [openEmail, setOpenEmail] = useState(false)

  const [columns, setColumns] = useState(() => {
    const columnsKey = getPrefKey('columnsVis')
    const base = options.columns || []
    const saved = sessionStorage.getItem(columnsKey) || localStorage.getItem(columnsKey)
    if (!saved) return base
    try {
      const vis = JSON.parse(saved)
      return base.map((c) => ({ ...c, isVisible: vis[c.name] ?? c.isVisible }))
    } catch {
      return base
    }
  })

  useEffect(() => {
    const columnsKey = getPrefKey('columnsVis')
    const vis = Object.fromEntries(columns.map((c) => [c.name, c.isVisible ?? true]))
    const json = JSON.stringify(vis)
    sessionStorage.setItem(columnsKey, json)
    localStorage.setItem(columnsKey, json)
    setColumnsParent?.(columns)
  }, [columns, type, setColumnsParent, userId])

  const [isRefresh, setIsRefresh] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const isMenuOpen = Boolean(anchorEl)
  const isSubMod = options?.isSubMod ?? false
  const hideButtonsEdit = options.hideButtonsInEdit ?? true
  const hideButtonsAdd = options.hideButtonsInAdd ?? true
  const [query, setQuery] = useState(() => {
    const filtersKey = getPrefKey('filters')
    const saved = sessionStorage.getItem(filtersKey) || localStorage.getItem(filtersKey)
    if (!saved) return { combinator: 'and', rules: [] }

    try {
      const parsedMongo = JSON.parse(saved)
      setShowFilters(true)
      return parseMongoDB(parsedMongo)
    } catch {
      return { combinator: 'and', rules: [] }
    }
  })

  useEffect(() => {
    const filtersKey = getPrefKey('filters')
    const saved = sessionStorage.getItem(filtersKey) || localStorage.getItem(filtersKey)
    if (saved) {
      const parsedMongo = JSON.parse(saved)
      setFilters(formatQuery(parseMongoDB(parsedMongo), { format: 'mongodb', fallbackExpression: 'null' }))
    }
  }, [type, setFilters, userId])

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)

  useEffect(() => {
    if (isRefresh) {
      setIsRefresh(false)
      setIsUpdated(true)
    }
  }, [isRefresh, setIsUpdated])

  const changeVisibility = (_e, colName) => {
    setColumns((prev) => prev.map((c) => (c.name === colName ? { ...c, isVisible: !(c.isVisible ?? true) } : c)))
  }

  const resetColumns = () => {
    const columnsKey = getPrefKey('columnsVis')
    setColumns(options.columns)
    sessionStorage.removeItem(columnsKey)
    localStorage.removeItem(columnsKey)
  }

  const buttonStyle = isSubMod
    ? {
        width: 40,
        height: 40,
        minWidth: 40,
        minHeight: 40,
        backgroundColor: muiTheme.palette.primary.main,
        color: 'white',
        '&:hover': {
          backgroundColor: muiTheme.palette.primary.dark
        },
        '&.Mui-disabled': {
          backgroundColor: '#ccc',
          color: '#eee',
          boxShadow: 'none'
        },
        boxShadow: 'none'
      }
    : {
        width: 40,
        height: 40,
        minWidth: 40,
        minHeight: 40,
        backgroundColor: 'white',
        color: muiTheme.palette.primary.main,
        '&:hover': {
          backgroundColor: muiTheme.palette.primary.light,
          color: 'white'
        },
        '&.Mui-disabled': {
          backgroundColor: '#f0f0f0',
          color: '#aaa',
          boxShadow: 'none'
        },
        boxShadow: 'none'
      }

  const bannerStyle = {
    mb: 2,
    px: 3,
    py: 2,
    borderRadius: 3,
    color: isSubMod ? muiTheme.palette.primary.main : 'white',
    background: isSubMod
      ? 'white'
      : `linear-gradient(90deg, ${muiTheme.palette.primary.main}, ${muiTheme.palette.primary.dark})`,
    border: isSubMod ? `1px solid ${muiTheme.palette.primary.main}` : 'none',
    boxShadow: 3
  }

  const buildFilter = () => {
    if (!showFilters) return null
    const filters = columns.filter((item) => item.isFilter)
    if (!filters.length) return null
    return (
      <Box py={1}>
        <Paper variant='outlined'>
          <Card variant='outlined'>
            <Box sx={{ p: 0 }}>
              <Grid2 container>
                <Grid2>
                  <ThemeProvider theme={muiTheme}>
                    <QueryBuilderMaterial>
                      <QueryBuilder
                        controlElements={{ valueEditor: CustomDateEditor }}
                        fields={filters}
                        query={query}
                        onQueryChange={setQuery}
                        operators={[
                          { name: '=', value: '=', label: '=' },
                          { name: '!=', value: '!=', label: '!=' },
                          { name: '<', value: '<', label: '<' },
                          { name: '>', value: '>', label: '>' },
                          { name: '<=', value: '<=', label: '<=' },
                          { name: '>=', value: '>=', label: '>=' },
                          { name: 'contains', value: 'contains', label: 'contient' },
                          { name: 'beginsWith', value: 'beginsWith', label: 'commence par' },
                          { name: 'endsWith', value: 'endsWith', label: 'finit par' },
                          { name: 'doesNotContain', value: 'doesNotContain', label: 'ne contient pas' },
                          { name: 'doesNotBeginWith', value: 'doesNotBeginWith', label: 'ne commence pas par' },
                          { name: 'doesNotEndWith', value: 'doesNotEndWith', label: 'ne finit pas par' },
                          { name: 'null', value: 'null', label: 'est null' },
                          { name: 'notNull', value: 'notNull', label: "n'est pas null" },
                          { name: 'in', value: 'in', label: 'dans' },
                          { name: 'notIn', value: 'notIn', label: "n'est pas dans" },
                          { name: 'between', value: 'between', label: 'entre' },
                          { name: 'notBetween', value: 'notBetween', label: "n'est pas entre" }
                        ]}
                        combinators={[
                          { name: 'and', value: 'and', label: 'ET' },
                          { name: 'or', value: 'or', label: 'OU' }
                        ]}
                        translations={{
                          addRule: { label: '+ Règle', title: 'Ajouter règle' },
                          addGroup: { label: '+ Groupe', title: 'Ajouter groupe' },
                          combinators: { title: 'Opérateurs' },
                          fields: { title: 'Champs' },
                          operators: { title: 'Opérateurs' },
                          value: { title: 'Valeur' },
                          removeRule: { label: '⨯', title: 'Supprimer règle' },
                          removeGroup: { label: '⨯', title: 'Supprimer groupe' }
                        }}
                      />
                    </QueryBuilderMaterial>
                  </ThemeProvider>
                </Grid2>
              </Grid2>
            </Box>
            <CardActions>
              <Button
                onClick={() => {
                  const filtersKey = getPrefKey('filters')
                  setQuery({ combinator: 'and', rules: [] })
                  setFilters(JSON.stringify({}))
                  sessionStorage.removeItem(filtersKey)
                  localStorage.removeItem(filtersKey)
                }}
              >
                Effacer
              </Button>
              <Button
                onClick={() => {
                  const mongoObj = formatQuery(query, { format: 'mongodb', fallbackExpression: 'null' })
                  if (mongoObj !== '{null}') {
                    setFilters(mongoObj)
                    const json = JSON.stringify(mongoObj)
                    const filtersKey = getPrefKey('filters')
                    sessionStorage.setItem(filtersKey, json)
                    localStorage.setItem(filtersKey, json)
                  }
                }}
              >
                Rechercher
              </Button>
            </CardActions>
          </Card>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} style={style}>
      <Box sx={bannerStyle}>
        <Stack direction='column' spacing={0.5}>
          <Typography variant='h6' sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
            {typeof options.title === 'function' ? options.title() : options.title}
          </Typography>
        </Stack>
        {!(hideButtonsEdit && location.pathname.includes('/edit')) &&
          !(hideButtonsAdd && location.pathname.includes('/add')) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                {options.isFilter && (
                  <Tooltip title='Afficher/Masquer filtres'>
                    <IconButton onClick={() => setShowFilters(!showFilters)} sx={buttonStyle}>
                      <FilterAltIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {options.customComponentsLeft?.map((component, i) => (
                  <Box key={i}>{component.component()}</Box>
                ))}
              </Stack>

              <Stack direction='row' spacing={1} alignItems='center'>
                {options.customActionsRight?.map((action, i) => (
                  <Tooltip title={action.tooltip} key={i}>
                    <span>
                      {action.component ? (
                        action.component()
                      ) : (
                        <Fab size='small' sx={buttonStyle} disabled={action.disabled?.()} onClick={action.onClick}>
                          {action.icon()}
                        </Fab>
                      )}
                    </span>
                  </Tooltip>
                ))}

                {options.columnVisibility && (
                  <Tooltip title='Colonnes'>
                    <Fab size='small' sx={buttonStyle} onClick={handleOpenMenu}>
                      <ViewColumnIcon />
                    </Fab>
                  </Tooltip>
                )}

                {options.actions?.add && options.accessRight.is_add && (
                  <Tooltip title='Nouveau'>
                    <Fab
                      size='small'
                      sx={buttonStyle}
                      component={Link}
                      state={{ history: `/${type}` }}
                      to={`/${type}/add`}
                    >
                      <AddIcon />
                    </Fab>
                  </Tooltip>
                )}
                {options.actions?.addFor && options.accessRight.is_add && (
                  <Tooltip title='Nouveau'>
                    <Fab
                      size='small'
                      sx={buttonStyle}
                      aria-label='add'
                      component={Link}
                      state={{
                        history: `/${type}`,
                        context: options.getContext()
                      }}
                      to={`/${type}/add`}
                    >
                      <AddIcon />
                    </Fab>
                  </Tooltip>
                )}

                {options.actions?.sendMail && (
                  <>
                    <Tooltip title='Nouveau Mail'>
                      <Fab size='small' sx={buttonStyle} onClick={() => setOpenEmail(true)}>
                        <AddIcon />
                      </Fab>
                    </Tooltip>
                    <EmailDialog
                      open={openEmail}
                      isNewEmail
                      email={options.getContext()}
                      setOpen={setOpenEmail}
                      setIsUpdated={setIsUpdated}
                    />
                  </>
                )}

                {options.actions?.files && (
                  <>
                    <Tooltip title='Pièces jointes'>
                      <Fab size='small' sx={buttonStyle} onClick={() => setOpen(true)}>
                        <AttachFileIcon />
                      </Fab>
                    </Tooltip>
                    <FilesDialog
                      objectType={type}
                      id={location?.state?.id}
                      open={open}
                      setObject={setObject}
                      parentObject={parentObject}
                      setParentObject={setParentObject}
                      onClose={() => setOpen(false)}
                    />
                  </>
                )}

                {options.actions?.audits && (
                  <>
                    <Tooltip title='Audits'>
                      <Fab size='small' sx={buttonStyle} onClick={() => setOpenAudit(true)}>
                        <IconContext.Provider value={{ size: '24px' }}>
                          <AiOutlineAudit />
                        </IconContext.Provider>
                      </Fab>
                    </Tooltip>
                    <AuditDialog
                      objectType={type}
                      modelType={modelType}
                      id={location?.state?.id}
                      open={openAudit}
                      onClose={() => setOpenAudit(false)}
                    />
                  </>
                )}

                {options.actions?.edit && options.accessRight.is_edit && (
                  <Tooltip title='Modifier'>
                    <Fab
                      size='small'
                      sx={buttonStyle}
                      component={Link}
                      state={{ id: object?.id, history: location.pathname }}
                      to={location.pathname.includes('myprofile') ? `/myprofile/edit` : `/${type}/edit`}
                      disabled={options.actions.editDisabled?.()}
                    >
                      <EditIcon />
                    </Fab>
                  </Tooltip>
                )}

                {options.actions?.list && (
                  <Tooltip title='Retour liste'>
                    <Fab size='small' sx={buttonStyle} component={Link} to={`/${type}`}>
                      <FormatListNumberedIcon />
                    </Fab>
                  </Tooltip>
                )}

                {options.actions?.back && (
                  <Tooltip title='Retour'>
                    <Fab size='small' sx={buttonStyle} onClick={() => navigate(-1)}>
                      <ArrowBackIcon />
                    </Fab>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          )}

        {options.isFilter && query.rules?.length > 0 && (
          <Chip
            label='Filtres actifs'
            size='small'
            sx={{
              mt: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white'
            }}
          />
        )}

        {options.columnVisibility && columns?.length > 0 && (
          <MenuVisibility
            columns={columns || []}
            changeVisibility={changeVisibility}
            isMenuOpen={isMenuOpen}
            anchorEl={anchorEl}
            handleCloseMenu={handleCloseMenu}
            resetColumns={resetColumns}
          />
        )}
      </Box>

      <Collapse in={showFilters} timeout={300} unmountOnExit>
        <Box sx={{ mt: 1 }}>{options.isFilter && buildFilter()}</Box>
      </Collapse>
    </Box>
  )
}

export default Toolbar
